'use client';

import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface EmptyConceptCardProps {
  onClick: () => Promise<void>;
  disabled?: boolean;
  className?: string;
}

export function EmptyConceptCard({
  onClick,
  disabled = false,
  className,
}: EmptyConceptCardProps) {
  const [isCreating, setIsCreating] = useState(false);

  const handleClick = async () => {
    if (disabled || isCreating) return;

    try {
      setIsCreating(true);
      await onClick();
    } catch (error) {
      console.error('Failed to create concept:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30 cursor-pointer transition-all relative',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:shadow-lg hover:scale-[1.01] hover:border-muted-foreground/50',
        className
      )}
      onClick={handleClick}
    >
      {/* Main content area - determines aspect ratio */}
      <div className="aspect-[4/3] bg-transparent"></div>

      {/* Plus icon - absolutely centered over the entire card */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Plus
          className={cn(
            'w-9 h-9 transition-colors',
            disabled ? 'text-gray-400' : 'text-muted-foreground'
          )}
          strokeWidth={1.5}
        />
      </div>

      {/* Actions bar - matching ConceptCard structure for consistent height */}
      <div className="p-3 bg-transparent">
        <div className="h-5">&nbsp;</div>
      </div>
    </div>
  );
}
